import _ from 'underscore';
import Backbone from 'backbone';
import ConsentText from 'models/gdpr/Consent';
import GdprUtil from 'utils/gdpr/GdprUtil';
import GrantConsent from 'models/gdpr/consent/GrantConsent';
import viewerUserPreferences from 'models/user/ViewerUserPreferences';
import LocaleMapper from 'utils/LocaleMapper';
import PrivacyItem from 'views/gdpr/privacy/PrivacyItem';
import GdprFeature from 'models/gdpr/Feature';
import LocationUtil from 'utils/LocationUtil';
import ModelSynchronizer from 'utils/ModelSynchronizer';
import Preference from 'models/preferences/Preferences';
import Localizer from 'localizer';
import templateHtml from 'views/gdpr/privacy/templates/privacyContent.html';
import GdprFeatureUpdateInstance from './GdprFeatureUpdateInstance';

export default Backbone.View.extend({
    ALL_GRANTED: 'ALL_GRANTED',
    ENABLE_CONTINUE: 'ENABLE_CONTINUE',
    GRANT_ERROR: 'GRANT_ERROR',
    LOADED: 'LOADED',
    NOT_DISPLAYING: 'NOT_DISPLAYING',

    enableFeatureFlag: false,

    template: _.template(templateHtml),

    initialize: function(args) {
        this.fetchUserLocation();
        this.saveSuccess = args.saveSuccess;
    },

    fetchUserLocation: function() {
        LocationUtil.getUserLocation(
            function(userLocation) {
                let countryCode = userLocation.countryCode || '';
                // CN and KR users should check privacy consent
                if ('CN' !== countryCode.toUpperCase() && 'KR' !== countryCode.toUpperCase()) {
                    this.trigger(this.NOT_DISPLAYING);
                } else {
                    this.fetchFeatures();
                }
            }.bind(this),
            function() {
                this.fetchFeatures();
            }.bind(this)
        );
    },

    fetchFeatures: function() {
        var modelSynchronizer = new ModelSynchronizer();
        this.connectPrivacy = new GdprFeature({
            feature: GdprFeature.Features.CONNECT_PRIVACY
        });

        this.golfPrivacy = new GdprFeature({
            feature: GdprFeature.Features.GOLF_PRIVACY
        });

        this.ccslEnabled = new Preference({ key: 'enable-ccsl-consent' });

        modelSynchronizer.addModel({ model: this.connectPrivacy, required: true });
        modelSynchronizer.addModel({ model: this.golfPrivacy, required: true });
        modelSynchronizer.addModel({ model: this.ccslEnabled, required: true });

        modelSynchronizer.on(
            ModelSynchronizer.Events.SYNCHRONIZED,
            function() {
                this.handleFeatures();
            },
            this
        );
        modelSynchronizer.fetchModels();
    },

    handleFeatures: function() {
        this.featureEnabled = this.ccslEnabled.get('value') === 'true';
        this.displayConnectPrivacy = this.featureEnabled && GdprUtil.shouldAskConsentFor(this.connectPrivacy.toJSON());
        this.consentExpired = this.featureEnabled && GdprUtil.shouldShowExpiredText(this.connectPrivacy.toJSON());
        this.displayGolfPrivacy = this.featureEnabled && GdprUtil.shouldAskConsentFor(this.golfPrivacy.toJSON());
        this.connectPrivacyConsented = !this.displayConnectPrivacy;
        this.golfPrivacyConsented = !this.displayGolfPrivacy;
        this.consented = {};
        this.loadTexts();
    },

    loadTexts: function() {
        var modelSynchronizer = new ModelSynchronizer();

        if (!(this.ccslEnabled.get('value') === 'true') || (!this.displayConnectPrivacy && !this.displayGolfPrivacy)) {
            this.trigger(this.NOT_DISPLAYING);
            return;
        }
        if (this.displayConnectPrivacy) {
            this.connectPrivacyText = new ConsentText({
                consentTypeId: GdprUtil.CONSENT_TYPE_CONNECT_PRIVACY,
                locale: this.getUserLocale()
            });
            modelSynchronizer.addModel({
                model: this.connectPrivacyText,
                required: true
            });
        }

        if (this.displayGolfPrivacy) {
            this.golfPrivacyText = new ConsentText({
                consentTypeId: GdprUtil.CONSENT_TYPE_GOLF_PRIVACY,
                locale: this.getUserLocale()
            });
            modelSynchronizer.addModel({
                model: this.golfPrivacyText,
                required: true
            });
        }

        modelSynchronizer.on(
            ModelSynchronizer.Events.SYNCHRONIZED,
            function() {
                this.render();
            },
            this
        );
        modelSynchronizer.fetchModels();
    },

    render: function() {
        if (!this.connectPrivacy && !this.golfPrivacy) {
            return;
        }

        if (this.shouldHideOverlay()) {
            this.trigger(this.NOT_DISPLAYING);
            return;
        }

        this.trigger(this.LOADED);

        this.$el.html(
            this.template({
                Localizer: Localizer
            })
        );

        if (this.consentExpired) {
            this.$('#privacyPolicyHeader').hide();
        } else {
            this.$('#privacyPolicyUpdatedHeader').hide();
            this.$('#privacyPolicyUpdatedDesc').hide();
        }

        if (this.displayConnectPrivacy) {
            this.renderPrivacy(this.connectPrivacyText, this.changeGrantedConnectPrivacy.bind(this));
        }

        if (this.displayGolfPrivacy) {
            this.renderPrivacy(this.golfPrivacyText, this.changeGrantedGolfPrivacy.bind(this));
        }
    },

    shouldHideOverlay: function() {
        return this.connectPrivacy && !this.displayConnectPrivacy && this.golfPrivacy && !this.displayGolfPrivacy;
    },

    renderPrivacy: function(privacyText, onChange) {
        var privacyItem = new PrivacyItem({
            privacyText: privacyText,
            onGrantChange: onChange
        });
        this.$('#privacyPolicyItems').append(privacyItem.render().el);
    },

    changeGrantedConnectPrivacy: function(granted) {
        this.connectPrivacyConsented = granted;
        this.checkAbleToContinue();
    },

    changeGrantedGolfPrivacy: function(granted) {
        this.golfPrivacyConsented = granted;
        this.checkAbleToContinue();
    },

    checkAbleToContinue: function() {
        if (this.connectPrivacyConsented && this.golfPrivacyConsented) {
            this.trigger(this.ENABLE_CONTINUE, true);
        } else {
            this.trigger(this.ENABLE_CONTINUE, false);
        }
    },

    getUserLocale: function() {
        return LocaleMapper.getFullLocale(viewerUserPreferences.get('preferredLocale'));
    },

    onContinue: function(e) {
        if (e) {
            e.preventDefault();
        }
        this.$('.continue-button').attr('disabled', 'true');
        if (this.displayConnectPrivacy) {
            var feature = this.connectPrivacyText.toJSON()[0];
            this.grantPrivacy(GdprUtil.CONSENT_TYPE_CONNECT_PRIVACY, feature.locale, feature.version);
        }
        if (this.displayGolfPrivacy) {
            var feature = this.golfPrivacyText.toJSON()[0];
            this.grantPrivacy(GdprUtil.CONSENT_TYPE_GOLF_PRIVACY, feature.locale, feature.version);
        } else if (!this.displayConnectPrivacy) {
            GdprFeatureUpdateInstance.resetGdprFeatureFetchState(); // reset fetch success state
            GdprFeatureUpdateInstance.reFetchGdprFeature('UPLOAD');
            this.trigger(this.ALL_GRANTED);
        }
    },

    grantPrivacy: function(consentTypeId, locale, version) {
        var grantConsent = new GrantConsent({
            consentTypeId: consentTypeId,
            consentLocale: locale,
            consentVersion: version
        });
        grantConsent.bind('sync', this.consentGranted.bind(this, consentTypeId), this);
        grantConsent.bind('error', this.consentGrantedFailed.bind(this, consentTypeId), this);
        grantConsent.save();
    },

    consentGranted: function(consentTypeId) {
        this.consented[consentTypeId] = true;
        this.checkAgreedToAll();
    },

    consentGrantedFailed: function(consentTypeId) {
        this.trigger(this.GRANT_ERROR, consentTypeId);
    },

    checkAgreedToAll: function() {
        var agreed = true;
        if (this.displayConnectPrivacy && !this.consented[GdprUtil.CONSENT_TYPE_CONNECT_PRIVACY]) {
            agreed = false;
        }

        if (this.displayGolfPrivacy && !this.consented[GdprUtil.CONSENT_TYPE_GOLF_PRIVACY]) {
            agreed = false;
        }
        if (agreed) {
            GdprFeatureUpdateInstance.resetGdprFeatureFetchState(); // reset fetch success state
            GdprFeatureUpdateInstance.reFetchGdprFeature('UPLOAD');
            this.trigger(this.ALL_GRANTED);
        }
    }
});
